import { mergeAttributes, Node } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import Suggestion from "@tiptap/suggestion";
import { CHIP_CLASS_NAME, CHIP_INLINE_COLORS } from "../../constants";
import type { ChipOption } from "../../types";
import { VariableChipNodeView } from "./VariableChipNodeView";

export interface VariableChipOptions {
  /** HTML attributes to add to the chip node */
  HTMLAttributes: Record<string, unknown>;
  /** Suggestion configuration for the {{ trigger */
  suggestion: Partial<SuggestionOptions<ChipOption>>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variableChip: {
      /** Insert a variable chip node */
      insertVariableChip: (attrs: {
        id: string;
        label: string;
        value: string;
        bgColor?: string;
        fontColor?: string;
      }) => ReturnType;
    };
  }
}

export const VariableChip = Node.create<VariableChipOptions>({
  name: "variableChip",

  group: "inline",

  inline: true,

  atom: true,

  selectable: true,

  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      suggestion: {
        char: "{{",
        pluginKey: new PluginKey("variableChipSuggestion"),
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("data-id") || element.getAttribute("id") || "",
        renderHTML: (attributes) => ({
          "data-id": attributes.id as string,
        }),
      },
      label: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute("data-label") ||
          element.querySelector(".chip-text")?.textContent ||
          element.textContent ||
          "",
        renderHTML: (attributes) => ({
          "data-label": attributes.label as string,
        }),
      },
      value: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-value") || "",
        renderHTML: (attributes) => ({
          "data-value": attributes.value as string,
        }),
      },
      bgColor: {
        default: null,
        parseHTML: (element) => {
          const chipContent = element.querySelector(
            "span[style*='background-color']",
          );
          if (chipContent instanceof HTMLElement) {
            return chipContent.style.backgroundColor || null;
          }
          return null;
        },
        renderHTML: () => ({}),
      },
      fontColor: {
        default: null,
        parseHTML: (element) => {
          const chipText = element.querySelector(".chip-text");
          if (chipText instanceof HTMLElement) {
            return chipText.style.color || null;
          }
          return null;
        },
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      // New format
      { tag: `span[data-type="variable-chip"]` },
      // Legacy Quill format
      { tag: `span.${CHIP_CLASS_NAME}` },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = (node.attrs.label as string) || "";
    const bgColor =
      (node.attrs.bgColor as string) || CHIP_INLINE_COLORS.backgroundColor;
    const fontColor =
      (node.attrs.fontColor as string) || CHIP_INLINE_COLORS.textColor;
    const borderColor = CHIP_INLINE_COLORS.borderColor;

    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-type": "variable-chip",
        class: CHIP_CLASS_NAME,
        contenteditable: "false",
        style: "display:inline-block; margin:0 2px;",
      }),
      [
        "span",
        {
          style: [
            "display:inline-flex",
            "align-items:center",
            "height:24px",
            "gap:8px",
            `border:1px solid ${borderColor}`,
            "border-radius:4px",
            "padding:2px 6px",
            `background-color:${bgColor}`,
            "cursor:default",
            "white-space:nowrap",
          ].join(";"),
        },
        [
          "span",
          {
            class: "chip-text",
            style: `font-size:12px; vertical-align:middle; color:${fontColor}; display:inline-flex; align-items:center;`,
          },
          label,
        ],
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChipNodeView);
  },

  addCommands() {
    return {
      insertVariableChip:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
